import type { Locale } from '@tms/i18n';

/**
 * Загрузка Яндекс Карт JS API 2.1 — один раз на вкладку, по требованию.
 *
 * Карта — единственный поставщик (A12): адрес, граф и подложка из одного источника, и
 * рисовать подложку OSM/Leaflet рядом с геокодером Яндекса нельзя. Поэтому, когда
 * ключа нет или Яндекс его отверг, экран показывает внятное состояние «карты нет», а
 * не карту другого поставщика.
 *
 * Скрипт подключается только экраном, которому нужна карта: 2.1 — это сотни килобайт,
 * и платить ими за открытие ленты заказов незачем.
 *
 * Язык карты задаётся при загрузке и потом не меняется: 2.1 не умеет перезагружаться
 * на другой язык в той же вкладке. Переключение языка кабинета меняет подписи формы
 * сразу, а подписи карты — после перезагрузки страницы.
 */

export interface YandexEvent {
  get(name: 'coords'): [number, number];
  get(name: string): unknown;
}

export interface YandexEventManager {
  add(type: string, callback: (event: YandexEvent) => void): YandexEventManager;
}

export interface YandexGeometry {
  getCoordinates(): unknown;
  setCoordinates(coordinates: unknown): void;
  events: YandexEventManager;
}

export interface YandexGeoObject {
  geometry: YandexGeometry;
  events: YandexEventManager;
  options: { set(key: string, value: unknown): void };
  editor?: {
    startDrawing(): void;
    startEditing(): void;
    stopEditing(): void;
    stopDrawing(): void;
  };
}

export interface YandexMap {
  geoObjects: {
    add(object: YandexGeoObject): void;
    remove(object: YandexGeoObject): void;
    getBounds(): [[number, number], [number, number]] | null;
  };
  events: YandexEventManager;
  setCenter(center: readonly [number, number], zoom?: number): void;
  getZoom(): number;
  setBounds(
    bounds: [[number, number], [number, number]],
    options?: { checkZoomRange?: boolean; zoomMargin?: number },
  ): void;
  container: { fitToViewport(): void };
  destroy(): void;
}

export interface YandexMapsApi {
  ready(onReady: () => void, onError?: (error: unknown) => void): void;
  Map: new (
    element: HTMLElement,
    state: { center: readonly [number, number]; zoom: number; controls: string[] },
    options?: Record<string, unknown>,
  ) => YandexMap;
  Placemark: new (
    coordinates: readonly [number, number],
    properties?: Record<string, unknown>,
    options?: Record<string, unknown>,
  ) => YandexGeoObject;
  Polygon: new (
    contours: unknown,
    properties?: Record<string, unknown>,
    options?: Record<string, unknown>,
  ) => YandexGeoObject;
}

declare global {
  interface Window {
    ymaps?: YandexMapsApi;
  }
}

const SCRIPT_ORIGIN = 'https://api-maps.yandex.ru/2.1/';

/**
 * Сколько ждать загрузки скрипта и готовности API. Это не эксплуатационный порог, а
 * граница ожидания интерфейса: после неё человек видит «карта не загрузилась» и
 * продолжает заполнять координаты руками, вместо вечного «Загрузка».
 */
export const MAP_LOAD_TIMEOUT_MS = 15_000;

/** Язык карты по языку кабинета: у 2.1 нет `uz_Latn`, есть `uz_UZ`. */
export function yandexLanguage(locale: Locale): 'ru_RU' | 'uz_UZ' {
  return locale === 'ru' ? 'ru_RU' : 'uz_UZ';
}

export function yandexScriptUrl(apiKey: string, language: 'ru_RU' | 'uz_UZ'): string {
  const query = new URLSearchParams({ apikey: apiKey, lang: language });

  return `${SCRIPT_ORIGIN}?${query.toString()}`;
}

let loading: Promise<YandexMapsApi> | null = null;

/**
 * Загружает API. Повторный вызов отдаёт тот же результат; после отказа следующий вызов
 * пробует заново — ключ могли разрешить для домена, пока экран был открыт.
 */
export function loadYandexMaps(
  apiKey: string,
  language: 'ru_RU' | 'uz_UZ',
  timeoutMs: number = MAP_LOAD_TIMEOUT_MS,
): Promise<YandexMapsApi> {
  if (loading !== null) {
    return loading;
  }

  const attempt = new Promise<YandexMapsApi>((resolve, reject) => {
    const existing = window.ymaps;

    if (existing !== undefined) {
      existing.ready(() => resolve(existing), reject);

      return;
    }

    const script = document.createElement('script');
    const timer = window.setTimeout(() => reject(new Error('ymaps_timeout')), timeoutMs);
    const fail = (reason: string): void => {
      window.clearTimeout(timer);
      script.remove();
      reject(new Error(reason));
    };

    script.src = yandexScriptUrl(apiKey, language);
    script.async = true;
    /*
     * Ключ JS API ограничивается доменом по заголовку Referer. Кабинет отдаётся с
     * `Referrer-Policy: no-referrer` (NFR-924), и без этой строки Яндекс видит запрос
     * без домена и отвергает ключ, даже разрешённый для этого домена. Уходит только
     * источник (`https://tms.example.uz`), без пути и параметров.
     */
    script.referrerPolicy = 'strict-origin-when-cross-origin';
    script.onerror = () => fail('ymaps_script_failed');
    script.onload = () => {
      const api = window.ymaps;

      // Отвергнутый ключ: скрипт пришёл, но API не объявлено.
      if (api === undefined) {
        fail('ymaps_missing');

        return;
      }

      api.ready(
        () => {
          window.clearTimeout(timer);
          resolve(api);
        },
        () => fail('ymaps_not_ready'),
      );
    };

    document.head.appendChild(script);
  });

  loading = attempt;
  attempt.catch(() => {
    if (loading === attempt) {
      loading = null;
    }
  });

  return attempt;
}

/** Только для тестов: забыть загрузку, чтобы следующий тест начинал с чистой вкладки. */
export function resetYandexMapsForTests(): void {
  loading = null;
}

/**
 * Цвет из токенов темы. Карта рисует полигоны сама и CSS-переменных не понимает, а
 * писать цвет числом в коде запрещает дизайн-система: цвет берётся из вычисленного
 * стиля корня, и тёмная тема меняет его вместе с остальным интерфейсом.
 */
export function tokenColor(name: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}
